<template>
    <div
        class="grid sm:grid-cols-2 grid-cols-1 auto-rows-auto gap-4 sm:gap-2"
    >
        <div v-if="users && users.length > 1" class="col-span-1">
            <UiInput id="user_id" name="user_id">
                <template #label>
                    <label
                        for="user_id"
                        class="block text-sm font-medium leading-tight mb-2"
                    >
                        <span
                            class="text-sm font-medium leading-5 text-slate-600 dark:text-slate-300"
                        >
                            User
                        </span>
                    </label>
                </template>
                <template #input>
                    <div class="relative" data-dropdown="user">
                        <button
                            type="button"
                            @click="$emit('toggle-user-dropdown')"
                            class="form-input w-full text-left flex items-center justify-between px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                        >
                            <div class="flex items-center gap-2">
                                <Icon
                                    icon="user"
                                    class="w-5 h-5 text-slate-800 dark:text-slate-100"
                                />
                                <span
                                    class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5"
                                >
                                    {{ selectedUserName || 'Select User...' }}
                                </span>
                            </div>
                            <Icon
                                icon="chevron-down"
                                class="w-4 h-4 text-slate-400"
                                :class="{
                                    'rotate-180': showUserDropdown,
                                }"
                            />
                        </button>
                        <div
                            v-if="showUserDropdown"
                            class="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg"
                            data-dropdown-options="user"
                        >
                            <div
                                v-for="user in users"
                                :key="user.id"
                                @click="$emit('select-user', user)"
                                class="px-4 py-2.5 text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer first:rounded-t-md last:rounded-b-md"
                            >
                                {{ user.name }}
                            </div>
                        </div>
                    </div>
                </template>
            </UiInput>
        </div>
        <div
            class="col-span-1"
            v-if="
                selectedCard?.card?.type === 'business' ||
                localCard?.card?.type === 'business'
            "
        >
            <UiInput id="business_id" name="business_id">
                <template #label>
                    <label
                        for="business_id"
                        class="block text-sm font-medium leading-tight mb-2"
                    >
                        <span
                            class="text-sm font-medium leading-5 text-slate-600 dark:text-slate-300"
                        >
                            Entity
                        </span>
                        <span
                            class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400"
                        >
                            (optional)
                        </span>
                    </label>
                </template>
                <template #input>
                    <div class="relative" data-dropdown="business">
                        <button
                            type="button"
                            @click="$emit('toggle-business-dropdown')"
                            class="form-input w-full text-left flex items-center justify-between px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                        >
                            <div class="flex items-center gap-2">
                                <Icon
                                    icon="building"
                                    class="w-5 h-5 text-slate-800 dark:text-slate-100"
                                />
                                <span
                                    class="text-sm font-normal leading-5"
                                    :class="
                                        localCard.business_id
                                            ? 'text-slate-800 dark:text-slate-100'
                                            : 'text-slate-500 dark:text-slate-400'
                                    "
                                >
                                    {{
                                        selectedBusinessName || 'Select entity'
                                    }}
                                </span>
                            </div>
                            <Icon
                                icon="chevron-down"
                                class="w-4 h-4 text-slate-400"
                                :class="{
                                    'rotate-180': showBusinessDropdown,
                                }"
                            />
                        </button>
                        <div
                            v-if="showBusinessDropdown"
                            class="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg"
                            data-dropdown-options="business"
                        >
                            <!-- No entity option - only show when there are no businesses -->
                            <div
                                v-if="businesses.length === 0"
                                @click="$emit('select-business', null)"
                                class="px-4 py-2.5 text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer first:rounded-t-md last:rounded-b-md"
                            >
                                No entity
                            </div>
                            <!-- Business entities with icon -->
                            <div
                                v-for="business in businesses"
                                :key="business.id"
                                @click="$emit('select-business', business)"
                                class="px-4 py-2.5 text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer flex items-center gap-2 first:rounded-t-md last:rounded-b-md"
                            >
                                <Icon icon="building" class="w-5 h-5" />
                                {{ business.name }}
                            </div>
                            <!-- Add new entity option -->
                            <div
                                @click="$emit('open-add-entity')"
                                class="px-4 py-2.5 text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer flex items-center gap-2 first:rounded-t-md last:rounded-b-md"
                            >
                                <Icon icon="plus" class="w-4 h-4" />
                                Add new entity
                            </div>
                        </div>
                    </div>
                </template>
            </UiInput>
        </div>
    </div>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';

defineProps({
    localCard: Object,
    selectedCard: Object,
    users: Array,
    businesses: Array,
    wallet: Object,
    selectedUserName: String,
    selectedBusinessName: String,
    showUserDropdown: Boolean,
    showBusinessDropdown: Boolean,
});

defineEmits([
    'toggle-user-dropdown',
    'toggle-business-dropdown',
    'select-user',
    'select-business',
    'open-add-entity',
]);
</script>
